import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteNav } from "../components/SiteNav";
import { SiteFooter } from "../components/SiteFooter";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "IDjFlcptza32AjjnT3Tc4HF0BA5s_bTbms1x3jjJ5Zo" },
      { title: "Acadly" },
      {
        name: "description",
        content:
          "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams.",
      },
      { name: "author", content: "Acadly" },
      { name: "theme-color", content: "#f7f2e8" },
      { property: "og:site_name", content: "Acadly" },
      { property: "og:title", content: "Acadly" },
      {
        property: "og:description",
        content:
          "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Acadly" },
      { name: "twitter:description", content: "Subject explainers, exam strategy, and study systems for Indian undergraduates. Economics-forward, written by someone who sat the same exams." },
      { property: "og:image", content: "https://acadly.in/og-default.png" },
      { name: "twitter:image", content: "https://acadly.in/og-default.png" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      { rel: "alternate", type: "application/rss+xml", title: "Acadly", href: "/rss.xml" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "alternate", hrefLang: "en-IN", href: "https://acadly.in/" },
      { rel: "alternate", hrefLang: "x-default", href: "https://acadly.in/" },
    ],
    scripts: [
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-5ND1M934HG",
        async: true,
      },
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-5ND1M934HG');",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Acadly",
          alternateName: ["acadly", "acadly.in", "Acadly Journal", "Acadly Notes"],
          url: "https://acadly.in/",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://acadly.in/blog?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Acadly",
          legalName: "Acadly",
          alternateName: ["acadly.in", "Acadly Notes"],
          url: "https://acadly.in/",
          logo: "https://acadly.in/logo.png",
          sameAs: ["https://dhairya.lol"],
          description: "Acadly is an academic publication offering study notes, exam strategies, and subject explainers for Indian undergraduates.",
          publishingPrinciples: "https://acadly.in/editorial-policy",
          correctionsPolicy: "https://acadly.in/corrections",
          contactPoint: {
            "@type": "ContactPoint",
            email: "hello@acadly.in",
            contactType: "customer service",
            url: "https://acadly.in/contact",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <div id="site-root" className="min-h-screen flex flex-col bg-background text-foreground transition-[filter,opacity] duration-300">
        <SiteNav />
        <main className="flex-1">
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <SiteFooter />
      </div>
    </QueryClientProvider>
  );
}
